<template>
  <ModalComponent
    :submitButtonText="$t('forwards.deals.pull_escrow.submit')"
    :hideSubmitButton="true"
    size="xl"
    @close="close"
    @setForceToggle="setForceToggle"
  >
    <template #title>{{ $t("forwards.deals.pull_escrow.title") }}</template>

    <template #body>
      <div class="p-1">
        <p
          v-if="commitmentGone"
          class="error-note mb-0"
          role="alert"
          data-testid="escrow-commitment-gone"
        >
          <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
          <span>{{ $t("forwards.deals.common.commitment_gone") }}</span>
        </p>

        <template v-else-if="results === null">
          <p class="text-body-tertiary">
            {{ $t("forwards.deals.pull_escrow.body") }}
          </p>

          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label" :for="companyId">{{
                $t("forwards.deals.pull_escrow.company")
              }}</label>
              <select
                :id="companyId"
                v-model="selectedCompanyId"
                class="form-select"
              >
                <option :value="null" disabled>
                  {{ $t("forwards.deals.pull_escrow.company_placeholder") }}
                </option>
                <option
                  v-for="member in members"
                  :key="member.company_id"
                  :value="member.company_id"
                >
                  {{ member.company_name }}
                </option>
              </select>
            </div>
            <div class="col-md-6">
              <label class="form-label" :for="portfolioId">{{
                $t("forwards.deals.pull_escrow.source_portfolio")
              }}</label>
              <select
                :id="portfolioId"
                v-model="selectedPortfolioId"
                class="form-select"
                :disabled="portfolioOptions.length === 0"
              >
                <option :value="null" disabled>
                  {{ $t("forwards.deals.pull_escrow.portfolio_placeholder") }}
                </option>
                <option
                  v-for="option in portfolioOptions"
                  :key="option.id"
                  :value="option.id"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>
          </div>

          <p class="form-text">
            {{ $t("forwards.deals.pull_escrow.allowed_sources") }}
          </p>

          <LoadingIndicatorComponent
            v-if="itemsLoading"
            inline
            :message="$t('forwards.deals.pull_escrow.loading_items')"
          />

          <PortfolioItemsTable
            v-else-if="visibleItems.length > 0"
            :selection="selection"
            :items="visibleItems"
            :enableCountEditing="true"
            :allowMultiCompanySelection="false"
            :isForAdmin="true"
            :defaultVisibleColumns="PULL_COLUMNS"
            :unselectablePortfolioTypes="UNSELECTABLE_TYPES"
            :hideActionBar="true"
            storageContext="forwards-pull-to-escrow"
            tableSize="compact"
          />
          <p
            v-else-if="itemsFailed"
            class="error-note"
            role="alert"
            data-testid="pull-items-failed"
          >
            <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
            <span>{{ $t("forwards.deals.pull_escrow.items_failed") }}</span>
          </p>
          <p
            v-else-if="selectedPortfolioId !== null"
            class="text-body-tertiary fs-10"
          >
            {{ $t("forwards.deals.pull_escrow.no_items") }}
          </p>

          <div class="advisory-note">
            <FontAwesomeIcon icon="fa-regular fa-circle-info" />
            <span>{{ afterMoveText }}</span>
          </div>

          <p v-if="errorMessage" class="error-note mb-0" role="alert">
            <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
            <span>{{ errorMessage }}</span>
          </p>
        </template>

        <template v-else>
          <p class="fw-semibold">
            {{
              $t("forwards.deals.pull_escrow.results_title", {
                moved: movedCount,
                failed: failedCount,
              })
            }}
          </p>
          <ul class="list-unstyled mb-0">
            <li
              v-for="(item, index) in results"
              :key="`${item.portfolio_item_id}-${index}`"
              :class="
                item.ok ? 'text-success-emphasis' : 'text-danger-emphasis'
              "
            >
              {{
                $t("forwards.deals.pull_escrow.result_line", {
                  count: item.count,
                  item: item.portfolio_item_id,
                })
              }}
              <span v-if="!item.ok"> — {{ item.reason }}</span>
            </li>
          </ul>
        </template>
      </div>
    </template>

    <template #footer>
      <button type="button" class="btn btn-link text-secondary" @click="close">
        {{
          results === null
            ? $t("forwards.deals.common.cancel")
            : $t("forwards.deals.common.close")
        }}
      </button>
      <ActionButtonWithSpinner
        v-if="results === null"
        :busy="busy"
        :disabled="!canSubmit"
        variant="btn-primary"
        data-testid="escrow-pull-submit"
        @click="submit"
      >
        {{ $t("forwards.deals.pull_escrow.submit") }}
      </ActionButtonWithSpinner>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import ActionButtonWithSpinner from "@/components/common/ActionButtonWithSpinner.vue";
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import ModalComponent from "@/components/common/ModalComponent.vue";
import { formatNumberWithSpaces } from "@/composables/formatUtils";
import PortfolioItemsTable from "@/dashboard/inventory_management/common/PortfolioItemsTable.vue";
import type { ColumnKey } from "@/dashboard/inventory_management/common/portfolioTableColumns";
import { usePortfolioItemsStore } from "@/dashboard/inventory_management/common/usePortfolioItemsStore";
import { usePortfolioSelection } from "@/dashboard/inventory_management/common/usePortfolioSelection";
import { forwardsApi } from "@/forwards2/forwardsApi";
import type {
  EscrowMoveItemResult,
  MandateMemberRef,
} from "@/forwards2/forwardsTypes";
import type { FlattenedPortfolioItem } from "@/types";
import { sortBy, uniqBy } from "lodash-es";
import { computed, ref, useId, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

/** `escrow_service.PULLABLE_PORTFOLIO_TYPES` — what a certificate may be pulled out of. */
const PULLABLE_PORTFOLIO_TYPES = [
  "UNALLOCATED",
  "HOLD",
  "VIRTUAL",
  "SPOT_MARKET_PRICE",
  "SPOT_STX",
  "SPOT_MIN_PRICE",
];

const PULL_COLUMNS: ColumnKey[] = [
  "count",
  "technology_type",
  "production_month",
  "portfolio_name",
  "production_device_name",
  "registry_region",
  "transfer_deadline",
];

/** Nothing outside the pullable set reaches the table, so nothing needs blocking inside it. */
const UNSELECTABLE_TYPES = new Set<string>();

/**
 * Pull certificates that flowed past the forwards step into a commitment's escrow.
 *
 * The producer keeps ownership — the certificates move to Soldera's escrow registry account and
 * are attributed to the commitment's slices, earliest delivery window first. Everything the
 * backend validates (membership, source type, span and cap, eligibility) is re-checked there; the
 * form only narrows the choice to what is plausible.
 *
 * A commitment that a refresh took off the deal while the modal was open is said so, and the pull
 * stays disabled until the modal is closed: there is nothing left to pull into.
 */
const props = withDefaults(
  defineProps<{
    commitmentId: number | null;
    /** The member companies of the commitment's mandate — the only owners it may pull from. */
    members: MandateMemberRef[];
    /** The commitment's escrow and committed volume, for the after-move line. */
    escrowedMwh: number;
    committedMwh: number;
    /** The commitment the modal was opened for is no longer on the deal. */
    commitmentGone?: boolean;
    busy?: boolean;
    errorMessage?: string | null;
    results?: EscrowMoveItemResult[] | null;
  }>(),
  { commitmentGone: false, busy: false, errorMessage: null, results: null }
);

const emit = defineEmits<{
  submit: [
    body: {
      commitment_id: number;
      company_id: number;
      source_portfolio_id: number;
      items: { portfolio_item_id: number; count: number }[];
    },
  ];
}>();

const model = defineModel<boolean>({ required: true });

const { t } = useI18n();
const { globalErrorHandler } = useGlobalHandlers();

const companyId = useId();
const portfolioId = useId();
const forceToggle = ref<(value: boolean) => void>();

/**
 * `ModalComponent` hands its toggle over during its own setup, before any watcher has run, so the
 * current model value is applied at once — otherwise a modal mounted already open stays hidden.
 */
const setForceToggle = (toggle: (value: boolean) => void) => {
  forceToggle.value = toggle;
  toggle(model.value);
};
const selectedCompanyId = ref<number | null>(null);
const selectedPortfolioId = ref<number | null>(null);
const companyItems = ref<FlattenedPortfolioItem[]>([]);
const itemsLoading = ref(false);
// A failed fetch leaves no portfolio to choose, so without this the modal would say nothing at all.
const itemsFailed = ref(false);
let itemsGeneration = 0;

const portfolioItemsStore = usePortfolioItemsStore();
const selection = usePortfolioSelection({
  policy: (item) => item.company_order_id === null,
});

const portfolioOptions = computed(() =>
  sortBy(
    uniqBy(
      companyItems.value.map((item) => ({
        id: item.portfolio_id,
        label: item.portfolio_name,
      })),
      (option) => option.id
    ),
    (option) => option.label
  )
);

const visibleItems = computed(() =>
  selectedPortfolioId.value === null
    ? []
    : companyItems.value.filter(
        (item) => item.portfolio_id === selectedPortfolioId.value
      )
);

const pulledMwh = computed(() =>
  [...selection.selectedItemIds].reduce(
    (total, itemId) => total + (selection.getSelectedCount(itemId) ?? 0),
    0
  )
);

const afterMoveText = computed(() =>
  t("forwards.deals.pull_escrow.after_move", {
    from: formatNumberWithSpaces(props.escrowedMwh),
    to: formatNumberWithSpaces(props.escrowedMwh + pulledMwh.value),
    total: formatNumberWithSpaces(props.committedMwh),
  })
);

const canSubmit = computed(
  () =>
    !props.busy &&
    !props.commitmentGone &&
    props.commitmentId !== null &&
    selectedCompanyId.value !== null &&
    selectedPortfolioId.value !== null &&
    pulledMwh.value > 0
);

const movedCount = computed(
  () => (props.results ?? []).filter((item) => item.ok).length
);
const failedCount = computed(
  () => (props.results ?? []).filter((item) => !item.ok).length
);

/**
 * The admin item feed, restricted to the pullable portfolio types of one company.
 *
 * `PortfolioItemsTable`'s selection resolves ids through the shared portfolio-items store, so the
 * fetched rows are published there as well as rendered.
 */
const loadItems = async (company: number) => {
  // A slow answer for the previous company must not land under this one's name.
  const generation = itemsGeneration;
  itemsLoading.value = true;
  try {
    const response = await forwardsApi.listPullableItems(
      company,
      PULLABLE_PORTFOLIO_TYPES
    );
    if (generation !== itemsGeneration) return;
    companyItems.value = response.data.portfolio_items ?? [];
    portfolioItemsStore.setAllItems(companyItems.value);
  } catch (error) {
    if (generation !== itemsGeneration) return;
    companyItems.value = [];
    portfolioItemsStore.setAllItems([]);
    itemsFailed.value = true;
    // Otherwise a broken fetch renders as "this company has nothing to pull" (RV-allocation-13).
    globalErrorHandler(error);
  } finally {
    if (generation === itemsGeneration) itemsLoading.value = false;
  }
};

watch(selectedCompanyId, (company) => {
  itemsGeneration++;
  itemsLoading.value = false;
  itemsFailed.value = false;
  selection.clearSelection();
  selectedPortfolioId.value = null;
  companyItems.value = [];
  if (company !== null) void loadItems(company);
});

watch(selectedPortfolioId, () => selection.clearSelection());

watch(model, (isOpen) => {
  forceToggle.value?.(isOpen);
  if (!isOpen) return;
  selection.clearSelection();
  selectedCompanyId.value = null;
  selectedPortfolioId.value = null;
  companyItems.value = [];
  itemsFailed.value = false;
});

const close = () => {
  model.value = false;
};

const submit = () => {
  if (
    !canSubmit.value ||
    props.commitmentId === null ||
    selectedCompanyId.value === null ||
    selectedPortfolioId.value === null
  )
    return;
  emit("submit", {
    commitment_id: props.commitmentId,
    company_id: selectedCompanyId.value,
    source_portfolio_id: selectedPortfolioId.value,
    items: [...selection.selectedItemIds].map((itemId) => ({
      portfolio_item_id: itemId,
      count: selection.getSelectedCount(itemId) ?? 0,
    })),
  });
};
</script>
